import { useState, useEffect } from "react";

import { useMutation } from "@apollo/react-hooks";
import { ImportProducts } from "./mutations";

import Papa from "papaparse";
import UploadHelper from "../../helpers/upload";
import { useMixpanel } from "react-mixpanel-browser";
import DefaultToaster from "../../atoms/DefaultToaster";
import { arrayToList } from "../../helpers/StringManipulation";
import { parseNetworkError } from "./helper";

const SHOPIFY = "Shopify";
const VIDEO_SQUIRREL = "Video Squirrel";
const VS_HEADERS = [
  "id",
  "name",
  "description",
  "image link",
  "image order",
  "price",
  "sku",
  "stock"
];

const SHOPIFY_HEADERS = [
  "Handle",
  "Title",
  "Body (HTML)",
  "Option1 Name",
  "Option1 Value",
  "Option2 Name",
  "Option2 Value",
  "Option3 Name",
  "Option3 Value",
  "Variant SKU",
  "Variant Price",
  "Variant Inventory Qty",
  "Image Src"
];

const validateHeaders = ({ headers }) => {
  if (VS_HEADERS.every((header) => headers.includes(header)))
    return VIDEO_SQUIRREL;
  else if (SHOPIFY_HEADERS.every((header) => headers.includes(header)))
    return SHOPIFY;
  else if (headers.includes("id")) {
    const remainingHeaders = VS_HEADERS.filter(
      (header) => !headers.includes(header)
    );
    throw new Error(
      `Identified CSV Format: ${VIDEO_SQUIRREL}.
      Following fields are missing from the uploaded csv: ${arrayToList(
        remainingHeaders
      )}`
    );
  } else if (headers.includes("Title")) {
    const remainingHeaders = SHOPIFY_HEADERS.filter(
      (header) => !headers.includes(header)
    );
    throw new Error(
      `Identified CSV Format: ${SHOPIFY}.
      Fields ${arrayToList(remainingHeaders)} are missing from the uploaded csv`
    );
  } else {
    throw new Error("Unrecognized CSV format");
  }
};

const getVideoSquirrelProducts = async ({ headers, data }) => {
  const products = [];
  const variants = [];

  const idIndex = headers.indexOf("id");
  const nameIndex = headers.indexOf("name");
  const descriptionIndex = headers.indexOf("description");
  const skuIndex = headers.indexOf("sku");
  const priceIndex = headers.indexOf("price");
  const stockIndex = headers.indexOf("stock");
  const parentHeaderIndex = headers.indexOf("parent");
  const imageIndex = headers.indexOf("image link");
  const imageOrderIndex = headers.indexOf("image order");
  const option1NameIndex = headers.indexOf("option1 name");
  const option1ValueIndex = headers.indexOf("option1 value");
  const option2NameIndex = headers.indexOf("option2 name");
  const option2ValueIndex = headers.indexOf("option2 value");
  const option3NameIndex = headers.indexOf("option3 name");
  const option3ValueIndex = headers.indexOf("option3 value");

  // Generate parent products
  await Promise.all(
    data.map(async (row) => {
      // if the row has a name, this is a parent product
      if (row[nameIndex] && row[idIndex]) {
        let image;
        try {
          image = await UploadHelper.uploadExternalImageToS3(row[imageIndex]);
        } catch (err) {
          throw Error(err);
        }

        const options = [
          row[option1NameIndex],
          row[option2NameIndex],
          row[option3NameIndex]
        ].filter((o) => o);

        products.push({
          product: {
            id: row[idIndex],
            name: row[nameIndex] || null,
            stock: row[stockIndex] === "" ? null : row[stockIndex],
            price: row[priceIndex] === "" ? null : row[priceIndex],
            sku: row[skuIndex] || null,
            description: row[descriptionIndex] || ""
          },
          images: [{ url: image, order: row[imageOrderIndex] }],
          options,
          variants: []
        });
      } else {
        variants.push(row);
      }
    })
  );

  // Handle variants
  await Promise.all(
    variants.map(async (row) => {
      // if the row doesn't contain a name and has image, this is an image
      if (row[idIndex] && row[imageIndex]) {
        const parentIndex = products.findIndex(
          (product) => product.product.id === row[idIndex]
        );

        const image = await UploadHelper.uploadExternalImageToS3(
          row[imageIndex]
        );

        products[parentIndex].images.push({
          url: image,
          order: row[imageOrderIndex]
        });
      }

      // if the row doesn't contain a name and has option value, this is a variant
      if (
        !row[idIndex] &&
        (row[option1ValueIndex] ||
          row[option2ValueIndex] ||
          row[option3ValueIndex])
      ) {
        const parentIndex = products.findIndex(
          (product) => product.product.id === row[parentHeaderIndex]
        );

        products[parentIndex].variants.push({
          stock: row[stockIndex] === "" ? null : row[stockIndex],
          price: row[priceIndex] === "" ? null : row[priceIndex],
          sku: row[skuIndex] || "",
          selectedValues: [
            row[option1ValueIndex]
              ? {
                  optionName: products[parentIndex].options[0],
                  valueName: row[option1ValueIndex]
                }
              : undefined,
            row[option2ValueIndex]
              ? {
                  optionName: products[parentIndex].options[1],
                  valueName: row[option2ValueIndex]
                }
              : undefined,
            row[option3ValueIndex]
              ? {
                  optionName: products[parentIndex].options[2],
                  valueName: row[option3ValueIndex]
                }
              : undefined
          ].filter((value) => value)
        });
      }
    })
  );

  for (const product of products) {
    product.product.id = undefined;
  }

  return products;
};

const getShopifyProducts = async ({ headers, data }) => {
  const products = [];
  const variants = [];

  const idIndex = headers.indexOf("Handle");
  const nameIndex = headers.indexOf("Title");
  const descriptionIndex = headers.indexOf("Body (HTML)");
  const skuIndex = headers.indexOf("Variant SKU");
  const priceIndex = headers.indexOf("Variant Price");
  const stockIndex = headers.indexOf("Variant Inventory Qty");
  const imageIndex = headers.indexOf("Image Src");
  const imageOrderIndex = headers.indexOf("Image Position");
  const option1NameIndex = headers.indexOf("Option1 Name");
  const option1ValueIndex = headers.indexOf("Option1 Value");
  const option2NameIndex = headers.indexOf("Option2 Name");
  const option2ValueIndex = headers.indexOf("Option2 Value");
  const option3NameIndex = headers.indexOf("Option3 Name");
  const option3ValueIndex = headers.indexOf("Option3 Value");

  // Generate parent products
  await Promise.all(
    data.map(async (row) => {
      // if the row has a name, this is a parent product
      if (row[nameIndex]) {
        let image;
        try {
          image = await UploadHelper.uploadExternalImageToS3(row[imageIndex]);
        } catch (err) {
          throw Error(err);
        }

        const options = [
          row[option1NameIndex],
          row[option2NameIndex],
          row[option3NameIndex]
        ].filter((o) => o);

        const selectedValues = [
          row[option1NameIndex]
            ? {
                optionName: row[option1NameIndex],
                valueName: row[option1ValueIndex]
              }
            : undefined,
          row[option2NameIndex]
            ? {
                optionName: row[option2NameIndex],
                valueName: row[option2ValueIndex]
              }
            : undefined,
          row[option3NameIndex]
            ? {
                optionName: row[option3NameIndex],
                valueName: row[option3ValueIndex]
              }
            : undefined
        ].filter((v) => v);

        products.push({
          product: {
            id: row[idIndex],
            name: row[nameIndex] || null,
            stock: row[stockIndex] === "" ? null : row[stockIndex],
            price: row[priceIndex] === "" ? null : row[priceIndex],
            sku: row[skuIndex] || null,
            description: row[descriptionIndex].replace(/<[^>]*>?/gm, ""),
            selectedValues
          },
          images: [{ url: image, order: row[imageOrderIndex] - 1 }],
          options,
          variants: []
        });
      } else {
        variants.push(row);
      }
    })
  );

  // Handle variants
  await Promise.all(
    variants.map(async (row) => {
      const parentIndex = products.findIndex(
        (product) => product.product.id === row[idIndex]
      );
      // if the row doesn't contain a name and has image, this is an image
      if (!row[nameIndex] && row[imageIndex]) {
        const image = await UploadHelper.uploadExternalImageToS3(
          row[imageIndex]
        );

        products[parentIndex].images.push({
          url: image,
          order: row[imageOrderIndex] - 1
        });
      }

      // if the row doesn't contain a name and has option value, this is a variant
      if (
        !row[nameIndex] &&
        (row[option1ValueIndex] ||
          row[option2ValueIndex] ||
          row[option3ValueIndex])
      ) {
        products[parentIndex].variants.push({
          stock: row[stockIndex] === "" ? null : row[stockIndex],
          price: row[priceIndex] === "" ? null : row[priceIndex],
          sku: row[skuIndex] || "",
          selectedValues: [
            row[option1ValueIndex]
              ? {
                  optionName: products[parentIndex].options[0],
                  valueName: row[option1ValueIndex]
                }
              : undefined,
            row[option2ValueIndex]
              ? {
                  optionName: products[parentIndex].options[1],
                  valueName: row[option2ValueIndex]
                }
              : undefined,
            row[option3ValueIndex]
              ? {
                  optionName: products[parentIndex].options[2],
                  valueName: row[option3ValueIndex]
                }
              : undefined
          ].filter((value) => value)
        });
      }
    })
  );

  for (const index in products) {
    // if the product doesn't have variants, remove options
    if (!products[index].variants.length) {
      products[index].options = null;
      products[index].variants = null;
      products[index].product.selectedValues = undefined;
    } else {
      // if the product has variants, make a new variant with the parent's info
      products[index].variants.push({
        stock: products[index].product.stock,
        price: products[index].product.price,
        sku: products[index].product.sku || "",
        selectedValues: products[index].product.selectedValues
      });

      products[index].product.sku = null;
      products[index].product.stock = null;
      products[index].product.price = null;
      products[index].product.selectedValues = undefined;
    }

    products[index].product.id = undefined;
  }

  return products;
};

const parseCSV = async (e) => {
  const files = Array.from(e);
  if (files.length) {
    return new Promise((resolve, reject) => {
      Papa.parse(files[0], {
        delimiter: ",",

        error: (err) => {
          alert(err);
        },

        complete: async (result) => {
          let products = [];
          const headers = result.data[0];

          try {
            let format = validateHeaders({ headers });
            if (format === SHOPIFY) {
              products = await getShopifyProducts({
                headers,
                data: result.data.slice(1)
              });
            }

            if (format === VIDEO_SQUIRREL) {
              products = await getVideoSquirrelProducts({
                headers,
                data: result.data.slice(1)
              });
            }
          } catch (err) {
            return reject(err);
          }

          resolve(products);
        }
      });
    });
  }
};

const useProductImport = ({
  setImportedProducts,
  isImportSidebarOpen,
  onImportSidebarClose
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState();
  const mixpanel = useMixpanel();
  const [importProducts] = useMutation(ImportProducts);

  const resetSidebar = () => {
    setImportedProducts([]);
    setError();
  };

  // Reset sidebar state on close
  useEffect(() => {
    if (!isImportSidebarOpen) {
      resetSidebar();
    }
  }, [isImportSidebarOpen]);

  const onFileChange = async (e) => {
    setUploading(true);

    mixpanel.track("Product List Upload CSV Attempted");

    let ProductCount;
    let VariantCount;
    let ImageCount;

    try {
      resetSidebar();
      const products = await parseCSV(e);

      setImportedProducts(products);
    } catch (err) {
      setError(err.message);

      mixpanel.track("Product List Upload CSV Failed", {
        ...(ProductCount && { ProductCount }),
        ...(VariantCount && { VariantCount }),
        ...(ImageCount && { ImageCount }),
        Reason: err.message
      });
    }

    setUploading(false);
  };

  const onSave = async (importedProducts) => {
    const ProductCount = importedProducts.length;
    const VariantCount = importedProducts.variants
      ? importedProducts.variants.length
      : 0;
    const ImageCount = importedProducts.images
      ? importedProducts.images.length
      : 0;

    let result;
    try {
      result = await importProducts({
        variables: { products: importedProducts },
        awaitRefetchQueries: true,
        refetchQueries: ["GetOwnedProducts"]
      });
      mixpanel.track("Product List Upload CSV Succeeded", {
        ProductCount,
        VariantCount,
        ImageCount,
        ProductIds:
          result && result.data && result.data.createProducts
            ? result.data.createProducts.map(({ id }) => id)
            : []
      });
      DefaultToaster.show({
        message: "Products uploaded successfully",
        icon: "tick-circle",
        timeout: 4000
      });
      resetSidebar();
      onImportSidebarClose();
    } catch (err) {
      setError(parseNetworkError(err));
      console.error(err);

      mixpanel.track("Product List Upload CSV Failed", {
        ...(ProductCount && { ProductCount }),
        ...(VariantCount && { VariantCount }),
        ...(ImageCount && { ImageCount }),
        Reason: `Upload to VideoSquirrel Server failure and ${err.message}`
      });
    }
  };

  return {
    onSave,
    uploading,
    onFileChange,
    error
  };
};

export default useProductImport;
